import type { ReactNode } from "react"

import { LogOut, Moon, Sun } from "lucide-react"

import { Button } from "@/components/ui/button"
import { useAppConfig, wordmarkParts } from "@/lib/app-config"
import { useIdentity } from "@/lib/identity"
import { useTheme } from "@/providers/ThemeProvider"

/** Official celver logo: black on light backgrounds, white on dark ones, per the CI. */
export function CelverLogo({ className = "" }: { className?: string }) {
  return (
    <>
      <img src="/brand/celver-logo-black.svg" alt="celver" className={`w-auto dark:hidden ${className}`} />
      <img src="/brand/celver-logo-white.svg" alt="celver" className={`hidden w-auto dark:block ${className}`} />
    </>
  )
}

/** The logo, a divider and the agent title set semibold. */
export function Wordmark({ className = "" }: { className?: string }) {
  const [, product] = wordmarkParts(useAppConfig().title)
  return (
    <div className={`flex items-center gap-2.5 ${className}`}>
      <CelverLogo className="h-5" />
      <span className="h-4 w-px bg-border" />
      <span className="text-[15px] font-semibold text-fg2">{product}</span>
    </div>
  )
}

export function Header({ menuSlot }: { menuSlot?: ReactNode }) {
  const { user: account, signOut: logout } = useIdentity()
  const { resolvedTheme, setTheme } = useTheme()

  return (
    <header className="flex h-15 shrink-0 items-center justify-between border-b border-border-subtle bg-card px-4">
      <div className="flex items-center gap-2.5">
        {menuSlot}
        <Wordmark />
      </div>

      <div className="flex items-center gap-1.5">
        {account && (
          <span className="mr-1 hidden rounded-lg bg-elevated px-3 py-1 text-xs text-fg3 sm:block">
            {account.name ?? account.username}
          </span>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
          aria-label="Toggle theme"
        >
          <Sun className="size-4 scale-100 rotate-0 transition-all dark:scale-0 dark:-rotate-90" />
          <Moon className="absolute size-4 scale-0 rotate-90 transition-all dark:scale-100 dark:rotate-0" />
        </Button>
        {account && (
          <Button
            variant="ghost"
            size="icon"
            onClick={logout}
            aria-label="Sign out"
            className="text-muted-foreground hover:text-destructive"
          >
            <LogOut className="size-4" />
          </Button>
        )}
      </div>
    </header>
  )
}
